var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { emptyGuid } from '../Shared/Constants';
import moment from 'moment-timezone';
import { ClinicalIndicationType, ConsentDetailStatus, CrossCheckType, InstructionType, MedicationType } from '../Contracts/MedicationModels';
import { isDaaInstruction, isDosageTimesInstruction, isFreeTextInstruction } from '../Contracts/PatientMedication.Response';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { externalWitnessSelection, freeTextClinicalIndicationSelection } from '../Shared/MedicationSharedMethods';
function mapOrderInstructionsForm(orderInstructions, type) {
    var _a, _b;
    if (isFreeTextInstruction(orderInstructions)) {
        return {
            type: InstructionType.FreeTextInstruction,
            textInstructions: orderInstructions.textInstructions,
            instructions: orderInstructions.instructions,
            routeOfAdministration: orderInstructions.routeOfAdministration
        };
    }
    else if (isDosageTimesInstruction(orderInstructions)) {
        return {
            type: InstructionType.DosageTimeInstruction,
            textInstructions: orderInstructions.textInstructions,
            instructions: orderInstructions.instructions,
            routeOfAdministration: orderInstructions.routeOfAdministration,
            frequency: orderInstructions.frequency,
            times: (_b = (_a = orderInstructions.times) === null || _a === void 0 ? void 0 : _a.map(function (time) { return [time.split(':')[0], time.split(':')[1]].join(':'); })) !== null && _b !== void 0 ? _b : [],
            startsOn: orderInstructions.startsOn
        };
    }
    if (type === MedicationType.EnrmcScript || type === MedicationType.ComplementaryTherapyOrder)
        return {
            type: InstructionType.DosageTimeInstruction,
            textInstructions: '',
            instructions: '',
            routeOfAdministration: null,
            times: [],
            startsOn: moment().format(ValidDateFormats.NodeDateTimeTimezone),
            frequency: null
        };
    return {
        type: InstructionType.FreeTextInstruction,
        textInstructions: '',
        instructions: '',
        routeOfAdministration: null
    };
}
function mapOrderInstructionsToContract(orderInstructions) {
    if (!orderInstructions) {
        return null;
    }
    else if (orderInstructions.type === InstructionType.FreeTextInstruction) {
        return {
            type: InstructionType.FreeTextInstruction,
            instructions: orderInstructions.instructions,
            routeOfAdministration: orderInstructions.routeOfAdministration
        };
    }
    else if (orderInstructions.type === InstructionType.DosageTimeInstruction) {
        return {
            type: InstructionType.DosageTimeInstruction,
            instructions: orderInstructions.instructions,
            routeOfAdministration: orderInstructions.routeOfAdministration,
            frequency: orderInstructions.frequency,
            times: orderInstructions.times.map(function (time) { return time + ":00"; }),
            startsOn: orderInstructions.startsOn
                ? moment(orderInstructions.startsOn, ValidDateFormats.NodeDateTimeTimezone).format(ValidDateFormats.NodeDateTimeTimezone)
                : ''
        };
    }
    return null;
}
function mapDosageInstructionsToContract(dosageInstructions) {
    if (!dosageInstructions)
        return null;
    if (dosageInstructions.type === InstructionType.DaaInstruction) {
        return {
            type: InstructionType.DaaInstruction,
            additionalInstructions: dosageInstructions.additionalInstructions,
            breakfast: isNaN(Number(dosageInstructions.breakfast)) ? 0 : Number(dosageInstructions.breakfast),
            dinner: isNaN(Number(dosageInstructions.dinner)) ? 0 : Number(dosageInstructions.dinner),
            lunch: isNaN(Number(dosageInstructions.lunch)) ? 0 : Number(dosageInstructions.lunch),
            nightTime: isNaN(Number(dosageInstructions.nightTime)) ? 0 : Number(dosageInstructions.nightTime),
            packagingType: dosageInstructions.packagingType
        };
    }
    else if (dosageInstructions.type === InstructionType.FreeTextInstruction) {
        return {
            type: InstructionType.FreeTextInstruction,
            instructions: dosageInstructions.instructions,
            routeOfAdministration: dosageInstructions.routeOfAdministration
        };
    }
    else if (dosageInstructions.type === InstructionType.DosageTimeInstruction) {
        return {
            type: InstructionType.DosageTimeInstruction,
            instructions: dosageInstructions.instructions,
            routeOfAdministration: dosageInstructions.routeOfAdministration,
            frequency: dosageInstructions.frequency,
            times: dosageInstructions.times.map(function (time) { return time + ":00"; }),
            startsOn: dosageInstructions.startsOn
                ? moment(dosageInstructions.startsOn, ValidDateFormats.NodeDateTimeTimezone).format(ValidDateFormats.NodeDateTimeTimezone)
                : ''
        };
    }
    return null;
}
function mapOrderInstructionToDosageInstructionContract(orderInstruction) {
    if (!orderInstruction)
        return null;
    if (orderInstruction.type === InstructionType.DaaInstruction) {
        return {
            type: InstructionType.FreeTextInstruction,
            instructions: orderInstruction.additionalInstructions,
            routeOfAdministration: null
        };
    }
    else if (orderInstruction.type === InstructionType.FreeTextInstruction) {
        return {
            type: InstructionType.FreeTextInstruction,
            instructions: orderInstruction.instructions,
            routeOfAdministration: orderInstruction.routeOfAdministration
        };
    }
    else if (orderInstruction.type === InstructionType.DosageTimeInstruction) {
        return {
            type: InstructionType.FreeTextInstruction,
            instructions: orderInstruction.instructions,
            routeOfAdministration: orderInstruction.routeOfAdministration
        };
    }
    return {
        type: InstructionType.FreeTextInstruction,
        instructions: '',
        routeOfAdministration: null
    };
}
function mapDosageInstructionsForm(dosageInstruction) {
    var _a;
    if (isFreeTextInstruction(dosageInstruction)) {
        return {
            type: InstructionType.FreeTextInstruction,
            textInstructions: dosageInstruction.textInstructions,
            instructions: dosageInstruction.instructions,
            routeOfAdministration: dosageInstruction.routeOfAdministration
        };
    }
    else if (isDosageTimesInstruction(dosageInstruction)) {
        return {
            type: InstructionType.DosageTimeInstruction,
            textInstructions: dosageInstruction.textInstructions,
            instructions: dosageInstruction.instructions,
            routeOfAdministration: dosageInstruction.routeOfAdministration,
            frequency: dosageInstruction.frequency,
            times: (_a = dosageInstruction.times) !== null && _a !== void 0 ? _a : [],
            startsOn: dosageInstruction.startsOn
        };
    }
    else if (isDaaInstruction(dosageInstruction)) {
        return {
            type: InstructionType.DaaInstruction,
            textInstructions: dosageInstruction.textInstructions,
            packagingType: dosageInstruction.packagingType,
            breakfast: dosageInstruction.breakfast.toString(),
            lunch: dosageInstruction.lunch.toString(),
            dinner: dosageInstruction.dinner.toString(),
            nightTime: dosageInstruction.nightTime.toString(),
            additionalInstructions: dosageInstruction.additionalInstructions
        };
    }
    return {
        type: InstructionType.FreeTextInstruction,
        textInstructions: '',
        instructions: '',
        routeOfAdministration: null
    };
}
export function convertContractToForm(contract) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23;
    var medication = contract.medication
        ? __assign(__assign({}, contract.medication), { originalMedication: contract.originalMedication }) : null;
    var dosageInstruction = mapDosageInstructionsForm(contract.dosageInstruction);
    var orderInstruction = mapOrderInstructionsForm(contract.orderInstruction, contract.medicationType);
    var dosageInstructionSameAsOrder = true;
    if (isFreeTextInstruction(dosageInstruction) &&
        (isFreeTextInstruction(orderInstruction) || isDosageTimesInstruction(orderInstruction))) {
        var instructionsAreDifferent = dosageInstruction.instructions !== orderInstruction.instructions ||
            ((_a = dosageInstruction.routeOfAdministration) === null || _a === void 0 ? void 0 : _a.code) !== ((_b = orderInstruction.routeOfAdministration) === null || _b === void 0 ? void 0 : _b.code);
        if (instructionsAreDifferent) {
            dosageInstructionSameAsOrder = false;
        }
    }
    return {
        authoringSystem: contract.authoringSystem,
        authoringSystemForeignKey: contract.authoringSystemForeignKey,
        updatedOn: contract.updatedOn,
        updatedBy: contract.updatedBy,
        medicationType: { value: contract.medicationType },
        name: contract.name,
        notes: (_c = contract.notes) !== null && _c !== void 0 ? _c : '',
        medication: medication,
        medicationCourse: contract.course,
        numberOfPacks: contract.productPackAmount.toString(),
        repeats: (_e = (_d = contract.repeatsAmount) === null || _d === void 0 ? void 0 : _d.toString()) !== null && _e !== void 0 ? _e : '',
        maximumSupplyPermitted: contract.maximumSupplyPermitted === 0 ? '' : (_g = (_f = contract.maximumSupplyPermitted) === null || _f === void 0 ? void 0 : _f.toString()) !== null && _g !== void 0 ? _g : '',
        isFreeTextMedication: contract.isFreeTextMedication,
        supplyAllRepeatsAtOnce: contract.supplyAllRepeatsAtOnce,
        scriptRetainedByPharmacy: contract.scriptRetainedByPharmacy,
        patientConsentsToActiveScriptList: contract.patientConsentsToActiveScriptList,
        patientConsentsToMyHrUpload: contract.patientConsentsToMyHrUpload,
        minimumDaysBetweenRepeats: contract.minimumDaysBetweenRepeats
            ? contract.minimumDaysBetweenRepeats.toString()
            : null,
        expiresOn: contract.expiresOn
            ? moment(contract.expiresOn, ValidDateFormats.IsoFormat).format(ValidDateFormats.Edit)
            : '',
        medicationExpiry: { value: contract.medicationExpiry },
        pharmacyRequestId: contract.pharmacyRequestId,
        performedOn: moment(contract.performedOn, ValidDateFormats.IsoFormat).format(ValidDateFormats.Edit),
        clinicalIndicationType: (_j = (_h = contract.clinicalIndication) === null || _h === void 0 ? void 0 : _h.type) !== null && _j !== void 0 ? _j : null,
        codedClinicalIndication: contract.clinicalIndication == null
            ? null
            : contract.clinicalIndication.type === ClinicalIndicationType.FreeText
                ? __assign({}, freeTextClinicalIndicationSelection) : contract.clinicalIndication.indication,
        freeTextClinicalIndication: ((_k = contract.clinicalIndication) === null || _k === void 0 ? void 0 : _k.type) === ClinicalIndicationType.FreeText
            ? (_l = contract.clinicalIndication) === null || _l === void 0 ? void 0 : _l.value
            : null,
        viewingRight: contract.viewingRight,
        problems: (_m = contract.problems) !== null && _m !== void 0 ? _m : [],
        isVerbalOrder: contract.verbalOrderDetail != null,
        authorisingPrescriber: (_p = (_o = contract.verbalOrderDetail) === null || _o === void 0 ? void 0 : _o.authorisingPrescriber) !== null && _p !== void 0 ? _p : null,
        witnessOneType: (_s = (_r = (_q = contract.verbalOrderDetail) === null || _q === void 0 ? void 0 : _q.witnessOne) === null || _r === void 0 ? void 0 : _r.type) !== null && _s !== void 0 ? _s : null,
        witnessOneProvider: ((_u = (_t = contract.verbalOrderDetail) === null || _t === void 0 ? void 0 : _t.witnessOne) === null || _u === void 0 ? void 0 : _u.type) === CrossCheckType.External
            ? __assign({}, externalWitnessSelection) : (_x = (_w = (_v = contract.verbalOrderDetail) === null || _v === void 0 ? void 0 : _v.witnessOne) === null || _w === void 0 ? void 0 : _w.provider) !== null && _x !== void 0 ? _x : null,
        witnessOneName: (_0 = (_z = (_y = contract.verbalOrderDetail) === null || _y === void 0 ? void 0 : _y.witnessOne) === null || _z === void 0 ? void 0 : _z.name) !== null && _0 !== void 0 ? _0 : null,
        witnessOneContactDetails: (_3 = (_2 = (_1 = contract.verbalOrderDetail) === null || _1 === void 0 ? void 0 : _1.witnessOne) === null || _2 === void 0 ? void 0 : _2.contactDetails) !== null && _3 !== void 0 ? _3 : null,
        allowBrandSubstitution: contract.allowBrandSubstitution,
        scheme: (_5 = (_4 = contract.paymentDetails) === null || _4 === void 0 ? void 0 : _4.type) !== null && _5 !== void 0 ? _5 : null,
        snapshot: (_7 = (_6 = contract.paymentDetails) === null || _6 === void 0 ? void 0 : _6.snapshot) !== null && _7 !== void 0 ? _7 : null,
        paymentDetailAuthorityNumber: (_10 = (_9 = (_8 = contract.paymentDetails) === null || _8 === void 0 ? void 0 : _8.paymentDetail) === null || _9 === void 0 ? void 0 : _9.authorityNumber) !== null && _10 !== void 0 ? _10 : '',
        paymentDetailApprovalNumber: (_13 = (_12 = (_11 = contract.paymentDetails) === null || _11 === void 0 ? void 0 : _11.paymentDetail) === null || _12 === void 0 ? void 0 : _12.approvalNumber) !== null && _13 !== void 0 ? _13 : '',
        paymentDetailReturnToPatient: (_16 = (_15 = (_14 = contract.paymentDetails) === null || _14 === void 0 ? void 0 : _14.paymentDetail) === null || _15 === void 0 ? void 0 : _15.returnToPatient) !== null && _16 !== void 0 ? _16 : false,
        paymentDetailPreviousAuthority: (_19 = (_18 = (_17 = contract.paymentDetails) === null || _17 === void 0 ? void 0 : _17.paymentDetail) === null || _18 === void 0 ? void 0 : _18.previousAuthority) !== null && _19 !== void 0 ? _19 : false,
        nonStandardPackSize: contract.nonStandardPackSize.toString(),
        isNonStandardPackSize: contract.isNonStandardPackSize,
        jurisdictionalControlledSubstanceReference: contract.jurisdictionalControlledSubstanceReference,
        nominatedDispensingPharmacy: contract.nominatedDispensingPharmacy,
        isUrgentSupplyScriptOwing: contract.isUrgentSupplyScriptOwing,
        consentDetailsDescription: (_21 = (_20 = contract.consentDetails) === null || _20 === void 0 ? void 0 : _20.description) !== null && _21 !== void 0 ? _21 : '',
        consentDetailsStatus: (_23 = (_22 = contract.consentDetails) === null || _22 === void 0 ? void 0 : _22.status) !== null && _23 !== void 0 ? _23 : ConsentDetailStatus.NotSet,
        dosageInstructionSameAsOrder: dosageInstructionSameAsOrder,
        dosageInstruction: dosageInstruction,
        orderInstruction: orderInstruction,
        isPsychotropicMedication: contract.isPsychotropicMedication,
        isAutoRemove: contract.isAutoRemove
    };
}
export function convertFormToContract(form) {
    var _a, _b;
    var paymentDetails = {};
    if (form.scheme) {
        paymentDetails.scheme = form.scheme;
    }
    paymentDetails.paymentDetail = {
        approvalNumber: form.paymentDetailApprovalNumber,
        authorityNumber: form.paymentDetailAuthorityNumber,
        previousAuthority: form.paymentDetailPreviousAuthority,
        returnToPatient: form.paymentDetailReturnToPatient
    };
    if (form.snapshot) {
        paymentDetails.snapshot = form.snapshot;
    }
    var minimumDaysBetweenRepeats = form.minimumDaysBetweenRepeats;
    if (form.medicationType.value !== MedicationType.TakeHomeScript) {
        minimumDaysBetweenRepeats = null;
    }
    var maximumSupplyPermitted = form.maximumSupplyPermitted;
    if ((form.medicationType.value === MedicationType.EnrmcScript ||
        form.medicationType.value === MedicationType.ComplementaryTherapyOrder) &&
        !form.maximumSupplyPermitted) {
        maximumSupplyPermitted = '0';
    }
    var repeats = form.repeats;
    if (form.medicationType.value === MedicationType.EnrmcScript ||
        form.medicationType.value === MedicationType.MedicationOrder ||
        form.medicationType.value === MedicationType.ComplementaryTherapyOrder) {
        repeats = '';
    }
    var dosageInstructionSameAsOrder = form.dosageInstructionSameAsOrder;
    if (form.medicationType.value === MedicationType.TakeHomeScript ||
        form.medicationType.value === MedicationType.MedicationHistory) {
        dosageInstructionSameAsOrder = false;
    }
    return {
        authoringSystem: form.authoringSystem,
        authoringSystemForeignKey: form.authoringSystemForeignKey,
        notes: form.notes,
        medicationType: (_b = (_a = form.medicationType) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null,
        medicationExpiry: form.medicationExpiry.value,
        pharmacyRequestId: form.pharmacyRequestId,
        paymentDetails: paymentDetails,
        course: form.medicationCourse,
        isFreeTextMedication: form.isFreeTextMedication,
        supplyAllRepeatsAtOnce: form.supplyAllRepeatsAtOnce,
        scriptRetainedByPharmacy: form.scriptRetainedByPharmacy,
        minimumDaysBetweenRepeats: minimumDaysBetweenRepeats ? Number(minimumDaysBetweenRepeats) : null,
        patientConsentsToActiveScriptList: form.patientConsentsToActiveScriptList,
        patientConsentsToMyHrUpload: form.patientConsentsToMyHrUpload,
        productPackAmount: form.numberOfPacks ? Number(form.numberOfPacks) : 0,
        repeatsAmount: repeats === '' ? null : Number(repeats),
        maximumSupplyPermitted: maximumSupplyPermitted ? Number(maximumSupplyPermitted) : null,
        expiresOn: form.expiresOn
            ? moment(form.expiresOn, ValidDateFormats.Edit).format(ValidDateFormats.IsoFormat)
            : null,
        performedOn: moment(form.performedOn, ValidDateFormats.Edit).format(ValidDateFormats.IsoFormat),
        viewingRight: form.viewingRight
            ? {
                code: form.viewingRight.code,
                name: form.viewingRight.name
            }
            : null,
        problems: form.problems
            ? form.problems.map(function (x) {
                return {
                    id: x.id,
                    authoringSystem: x.authoringSystem,
                    authoringSystemForeignKey: x.authoringSystemForeignKey,
                    name: x.name
                };
            })
            : [],
        allowBrandSubstitution: !!form.allowBrandSubstitution,
        nonStandardPackSize: form.nonStandardPackSize ? Number(form.nonStandardPackSize) : 0,
        isNonStandardPackSize: !!form.isNonStandardPackSize,
        clinicalIndication: form.clinicalIndicationType == null
            ? null
            : {
                type: form.clinicalIndicationType,
                indication: form.clinicalIndicationType === ClinicalIndicationType.Coded
                    ? form === null || form === void 0 ? void 0 : form.codedClinicalIndication
                    : null,
                value: form.clinicalIndicationType === ClinicalIndicationType.FreeText
                    ? form === null || form === void 0 ? void 0 : form.freeTextClinicalIndication
                    : null
            },
        verbalOrderDetail: {
            authorisingPrescriber: form.authorisingPrescriber
                ? {
                    id: form.authorisingPrescriber.id,
                    authoringSystem: form.authorisingPrescriber.authoringSystem,
                    authoringSystemForeignKey: form.authorisingPrescriber.authoringSystemForeignKey
                }
                : null,
            witnessOneProvider: form.witnessOneType === CrossCheckType.Provider && form.witnessOneProvider !== null
                ? {
                    crossCheckBy: form.updatedBy,
                    crossCheckOn: form.updatedOn,
                    provider: {
                        id: form.witnessOneProvider.id,
                        authoringSystem: form.witnessOneProvider.authoringSystem,
                        authoringSystemForeignKey: form.witnessOneProvider.authoringSystemForeignKey
                    }
                }
                : null,
            witnessOneExternal: form.witnessOneType === CrossCheckType.External &&
                form.witnessOneName !== null &&
                form.witnessOneContactDetails !== null
                ? {
                    crossCheckBy: form.updatedBy,
                    crossCheckOn: form.updatedOn,
                    name: form.witnessOneName,
                    contactDetails: form.witnessOneContactDetails
                }
                : null
        },
        jurisdictionalControlledSubstanceReference: form.jurisdictionalControlledSubstanceReference,
        nominatedDispensingPharmacy: form.nominatedDispensingPharmacy,
        isUrgentSupplyScriptOwing: form.isUrgentSupplyScriptOwing,
        consentDetails: {
            description: form.consentDetailsDescription,
            status: form.consentDetailsStatus
        },
        dosageInstruction: dosageInstructionSameAsOrder
            ? mapOrderInstructionToDosageInstructionContract(form.orderInstruction)
            : mapDosageInstructionsToContract(form.dosageInstruction),
        orderInstruction: mapOrderInstructionsToContract(form.orderInstruction),
        isAutoRemove: form.isAutoRemove
    };
}
export function convertFormToAdjustContract(form, service) {
    var dosageInstructionSameAsOrder = form.dosageInstructionSameAsOrder;
    if (form.medicationType.value === MedicationType.TakeHomeScript ||
        form.medicationType.value === MedicationType.MedicationHistory) {
        dosageInstructionSameAsOrder = false;
    }
    return {
        adjustedOn: moment().format(ValidDateFormats.NodeDateTimeTimezone),
        notes: form.notes,
        service: {
            id: emptyGuid,
            authoringSystem: service.authoringSystem,
            authoringSystemForeignKey: service.authoringSystemForeignKey
        },
        clinicalIndication: {
            type: form.clinicalIndicationType !== null ? form.clinicalIndicationType : null,
            indication: form.clinicalIndicationType === ClinicalIndicationType.Coded ? form === null || form === void 0 ? void 0 : form.codedClinicalIndication : null,
            value: form.clinicalIndicationType === ClinicalIndicationType.FreeText
                ? form === null || form === void 0 ? void 0 : form.freeTextClinicalIndication
                : null
        },
        consentDetails: {
            description: form.consentDetailsDescription,
            status: form.consentDetailsStatus
        },
        dosageInstruction: dosageInstructionSameAsOrder
            ? mapOrderInstructionToDosageInstructionContract(form.orderInstruction)
            : mapDosageInstructionsToContract(form.dosageInstruction),
        orderInstruction: mapOrderInstructionsToContract(form.orderInstruction)
    };
}
